/**
 * InstantTasks in-app bug reporter wiring for the Local Locker admin (web).
 *
 * Renders a Shadow-DOM modal with screenshot capture + annotator, a description
 * field, and submits the result as a Task against the configured InstantTasks
 * project via `@koderlabs/tasks-sdk-web-reporter`. Reports show up in
 * InstantTasks as a Ticket with `sourceType: 'reporter'`.
 *
 * Config (all optional — when the ingest key is absent the whole reporter
 * safely no-ops so the app never breaks for lack of telemetry):
 *   VITE_IT_INGEST_KEY  "<projectId>:<publicKey>"  e.g. "FE:pk_live_…"
 *   VITE_IT_ENDPOINT    base URL of the InstantTasks API host (NO trailing
 *                       /api/v1). Defaults to https://tasks.koderlabs.net.
 *
 * The floating bug FAB is rendered by the SDK (autoInject) and can be opened
 * with the `Cmd/Ctrl+Shift+B` hotkey. The project's In-App Reporter → `showFab`
 * admin setting can override the FAB visibility server-side.
 */
import { init } from "@koderlabs/tasks-sdk";
import { reporterIntegration } from "@koderlabs/tasks-sdk-web-reporter";

/** The reporter is both a core SDK Integration and the imperative WidgetApi. */
type Reporter = ReturnType<typeof reporterIntegration>;

/** "<projectId>:<publicKey>" combined ingest key. */
const INGEST_KEY = import.meta.env.VITE_IT_INGEST_KEY;
/** InstantTasks API host. Omitted → SDK defaults to prod. */
const ENDPOINT = import.meta.env.VITE_IT_ENDPOINT || undefined;

let reporter: Reporter | null = null;

/**
 * Initialise the web reporter. Idempotent; returns null when disabled
 * (no key) or called outside a browser (DOM-only widget).
 */
export function initBugReporter(): Reporter | null {
  if (reporter) return reporter;
  if (typeof window === "undefined") return null;
  if (!INGEST_KEY) {
    if (import.meta.env.DEV) {
      console.warn(
        "[tasks-sdk] VITE_IT_INGEST_KEY not set; bug reporter disabled",
      );
    }
    return null;
  }
  const [projectId, publicKey] = INGEST_KEY.split(":");
  if (!projectId || !publicKey) {
    if (import.meta.env.DEV) {
      console.warn(
        '[tasks-sdk] VITE_IT_INGEST_KEY malformed; expected "projectId:publicKey"',
      );
    }
    return null;
  }

  reporter = reporterIntegration({
    hotkey: "mod+shift+b", // 'mod' = Cmd on macOS, Ctrl elsewhere
    autoInject: true, // floating bug FAB; project showFab can override
    useNativeScreenshot: true, // getDisplayMedia, html2canvas fallback
    autoCapture: true, // snap on modal open
    // Admin already authenticates users; identity flows via setReporterUser,
    // not a typed-in form field.
    collectEmail: false,
  });

  init({ projectId, publicKey, endpoint: ENDPOINT, integrations: [reporter] });
  return reporter;
}

/**
 * Keep the reporter's identity in sync with the logged-in admin, so reports are
 * attributed to a real authenticated user. Pass null on logout to clear it.
 */
export function setReporterUser(
  user: { email?: string; fullName?: string } | null,
): void {
  if (!reporter) return;
  if (user?.email) {
    reporter.setReporter({ email: user.email, fullName: user.fullName });
  } else {
    reporter.clearReporter();
  }
}

/** Open the bug-report modal programmatically. No-op if not initialised. */
export function showBugReporter(): void {
  reporter?.show();
}
